import * as React from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  Bell,
  Trash,
  CaretDown,
  ArrowUpRight,
  FileText,
  FilmSlate,
  CheckCircle,
  ChartLineUp,
  Users,
  XCircle,
  ChatCircle,
  ClipboardText,
  Clock,
} from '../../design-system/icons';
import { toast } from 'sonner';
import { useNotifications, type AppNotification, type NotificationTab } from '@discovr/common';
import { conversations } from '@discovr/api';
import { cn } from '../../lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { UnreadBadge } from './UnreadBadge';
import { groupByDay } from './notificationDayGroups';
import { clubDayItems, clubHeadline, memberName, type NotificationClub } from './notificationClubs';
import { buildNotificationTarget, buildBatchActorTarget } from './notificationNavigation';
import { isThreadLockedError } from './notificationErrors';
import { normalizeSampleActors } from './notificationActors';

export interface NotificationBellProps {
  /** Route base for a campaign detail page. Both brand and admin currently use
   * `/campaign`; the prop exists so a future app can pass a different base
   * without a code change here. */
  campaignRouteBase?: string;
}

const THREAD_TYPE_PILLS: { label: string; value: string }[] = [
  { label: 'Negotiation', value: 'selection' },
  { label: 'Content', value: 'content' },
  { label: 'Script', value: 'script' },
];

const TAB_ICONS: Record<NotificationTab, typeof ChatCircle> = {
  messages: ChatCircle,
  activities: Clock,
};

// Expanded club rows visible before the member list scrolls internally:
// 6 rows × 42px min-height + padding ≈ the 264px cap below.
const MAX_VISIBLE_CLUB_ROWS = 6;

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.round(hrs / 24)}d`;
}

// A module-scope component (not a variable holding a component reference
// picked during render) so the react-hooks "static components" rule doesn't
// flag it — the icon choice still varies per notification `type`.
function ActivityIcon({ type, className }: { type: string; className?: string }) {
  const props = { className };
  if (type.includes('script')) return <FileText {...props} />;
  if (type.includes('content')) return <FilmSlate {...props} />;
  if (type === 'roster.status.live') return <ChartLineUp {...props} />;
  if (type === 'roster.status.accepted_by_brand' || type === 'roster.status.accepted_by_creator')
    return <Users {...props} />;
  if (type === 'roster.status.rejected_by_brand') return <XCircle {...props} />;
  if (type.includes('pending')) return <CheckCircle {...props} />;
  return <Bell {...props} />;
}

// Same module-scope rationale as ActivityIcon above. Every caller overrides
// the base size via `className` (size-8 cards, size-6 expanded rows) —
// cn()/twMerge resolves the conflicting size-* / text-* utilities.
const InitialsAvatar = ({ name, className }: { name?: string; className?: string }) => (
  <span
    className={cn(
      'flex size-9 shrink-0 items-center justify-center rounded-full bg-[#ecebe9] text-[14px] font-medium text-[#0e0c0a]',
      className,
    )}
  >
    {(name?.trim()?.[0] ?? '?').toUpperCase()}
  </span>
);

// Shared dismiss control used by message rows, single activity rows, the
// batched/clubbed card headers, and clubbed member rows. Reveals on hover of
// the nearest `group` ancestor (cards/headers) — member rows pass a
// `group-hover/row:` override and have no plain-`group` ancestor, so only
// the row-scoped reveal applies there — and always on keyboard focus.
function DismissButton({ onDelete, className }: { onDelete: () => void; className?: string }) {
  return (
    <button
      type="button"
      aria-label="Dismiss"
      onClick={(e) => {
        e.stopPropagation();
        onDelete();
      }}
      className={cn(
        'shrink-0 rounded-full p-1 text-[#79736d] opacity-0 transition-opacity hover:bg-background-destructive-light-hover hover:text-foreground-destructive-default focus-visible:opacity-100 group-hover:opacity-100',
        className,
      )}
    >
      <Trash size={14} />
    </button>
  );
}

export function NotificationBell({ campaignRouteBase = '/campaign' }: NotificationBellProps) {
  const {
    activeTab,
    setActiveTab,
    threadType,
    setThreadType,
    messages,
    activities,
    hasMoreMessages,
    hasMoreActivities,
    unreadCounts,
    loading,
    markAsRead,
    deleteNotif,
    markAllAsRead,
    loadMore,
    clearNewNotificationFlag,
    setPanelOpen,
  } = useNotifications();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set());

  // Guards against isPanelOpen sticking `true` forever if the bell unmounts
  // while the panel is open (e.g. route change while a Popover is open) —
  // without this, NotificationContext's isPanelOpenRef never flips back to
  // false and the toast-handler fan-out is suppressed for the rest of the
  // session.
  React.useEffect(() => () => setPanelOpen(false), [setPanelOpen]);

  const handleOpenChange = (next: boolean) => {
    if (next) clearNewNotificationFlag();
    setOpen(next);
    setPanelOpen(next);
  };

  const items = activeTab === 'messages' ? messages : activities;
  const hasMore = activeTab === 'messages' ? hasMoreMessages : hasMoreActivities;
  const groups = React.useMemo(() => groupByDay(items), [items]);
  const totalUnread = unreadCounts.messages + unreadCounts.activities;

  // Unified click-through: mark read, then close the panel and navigate when
  // a target resolved (mark-read still happens on a null target — clicking a
  // row is an explicit read either way).
  const openTarget = (n: AppNotification, target: { to: string; search: object } | null) => {
    if (!n.read) void markAsRead(n.id);
    if (!target) return;
    setOpen(false);
    setPanelOpen(false);
    void navigate({ to: target.to, search: target.search as never });
  };

  // Used by both Messages and Activities rows — a message card's body click
  // navigates even when it also supports an inline reply (spec §4.2).
  const goTo = (n: AppNotification) => openTarget(n, buildNotificationTarget(n, campaignRouteBase));

  const markReadOnly = (n: AppNotification) => {
    if (!n.read) void markAsRead(n.id);
  };

  // Per-row navigation for a batched-activity card's expanded actor list
  // (see BatchedActivityCard below) — resolves via buildBatchActorTarget
  // with an explicit actor username instead of the notification's own
  // creator_username (which doesn't identify any one actor in a batch).
  const goToActor = (n: AppNotification, username: string) =>
    openTarget(n, buildBatchActorTarget(n, username, campaignRouteBase));

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="icon-sm"
          aria-label={`Notifications${totalUnread ? ` (${totalUnread} unread)` : ''}`}
          className="relative text-foreground"
        >
          <Bell size={20} weight="light" />
          <UnreadBadge count={totalUnread} variant="floating" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={8}
        // Plain inline style for max-width, not a Tailwind arbitrary-value
        // class — Tailwind's JIT never generates a rule for this calc()
        // class (verified: no matching rule in any loaded stylesheet),
        // silently falling back to unconstrained width.
        style={{ maxWidth: 'calc(100vw - 2rem)' }}
        className="w-96 overflow-hidden rounded-[16px] border border-[#ecebe9] bg-white p-0 shadow-[0px_16px_40px_-10px_rgba(23,23,20,0.14)]"
      >
        <div className="flex flex-col gap-1.5 px-3 py-2">
          <div className="flex w-full items-center justify-between px-1 py-2.5">
            <h3 className="text-[16px] leading-6 font-medium text-[#0e0c0a]">Notifications</h3>
            <button
              type="button"
              onClick={() => void markAllAsRead()}
              disabled={totalUnread === 0}
              className={cn(
                'text-[13.5px] leading-5 font-medium hover:opacity-80 disabled:hover:opacity-100',
                totalUnread === 0 ? 'text-[#79736d]' : 'text-[#5a07ce]',
              )}
            >
              Mark all as read
            </button>
          </div>

          <div className="flex w-full items-end gap-6 border-b border-[#e5e5e5] px-1">
            {(['messages', 'activities'] as NotificationTab[]).map((tab) => {
              const TabIcon = TAB_ICONS[tab];
              const isActive = activeTab === tab;
              return (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  className="flex flex-1 cursor-pointer flex-col items-center gap-[10px] pt-1 pb-px"
                >
                  <span className="flex items-center gap-2 overflow-hidden px-0.5">
                    <TabIcon className={cn('size-5', isActive ? 'text-black' : 'text-[#79736d]')} />
                    <span
                      className={cn(
                        'text-[14px] leading-5 font-medium capitalize',
                        isActive ? 'text-black' : 'text-[#79736d]',
                      )}
                    >
                      {tab}
                    </span>
                    {unreadCounts[tab] > 0 && (
                      <span
                        className={cn(
                          'rounded-full bg-[#ecebe9] px-[7px] py-[2px] text-[11px] font-semibold leading-none',
                          isActive ? 'text-black' : 'text-[#79736d]',
                        )}
                      >
                        {unreadCounts[tab]}
                      </span>
                    )}
                  </span>
                  <span className={cn('h-[2px] w-full rounded-[2px]', isActive ? 'bg-black' : 'bg-transparent')} />
                </button>
              );
            })}
          </div>

          {activeTab === 'messages' && (
            <div className="flex w-full items-center pt-2 pb-1.5">
              <div className="flex w-full items-center rounded-[24px] bg-[#f4f4f2] p-1">
                {THREAD_TYPE_PILLS.map((pill) => (
                  <button
                    key={pill.value}
                    type="button"
                    onClick={() => setThreadType(pill.value)}
                    className={cn(
                      'flex flex-1 items-center justify-center rounded-[24px] px-2.5 py-1 text-[13px] leading-5 font-medium',
                      threadType === pill.value
                        ? 'bg-white text-[#171717] shadow-[0px_2px_3px_rgba(0,0,0,0.04),0px_1px_1px_rgba(0,0,0,0.04)]'
                        : 'text-[#737373]',
                    )}
                  >
                    {pill.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex w-full max-h-[70vh] flex-col gap-2 overflow-y-auto">
            {items.length === 0 && !loading ? (
              <EmptyState tab={activeTab} />
            ) : (
              <>
                {groups.map((group, index) => (
                  <React.Fragment key={`${group.label}-${index}`}>
                    <div className="flex w-full items-center gap-2 py-2 pr-4">
                      <span className="text-[12px] font-medium uppercase leading-4 text-[#737373]">{group.label}</span>
                      <div className="h-px min-w-0 flex-1 bg-[#ecebe9]" />
                    </div>
                    <ul className="flex w-full flex-col gap-1.5">
                      {activeTab === 'messages'
                        ? group.items.map((n) => (
                            <li key={n.id} className="w-full">
                              <MessageRow
                                notification={n}
                                onRowClick={() => goTo(n)}
                                onFocusMarkRead={() => markReadOnly(n)}
                                onDelete={() => deleteNotif(n.id)}
                              />
                            </li>
                          ))
                        : clubDayItems(group.items).map((entry) => {
                            if (entry.kind === 'club') {
                              // Expand/React key = day section (label+index —
                              // the index guards duplicate year-less labels,
                              // same defense as the fragment key above) +
                              // aggregation key: stable across member
                              // deletions (a first-member id would collapse
                              // the club when that member is dismissed) and
                              // distinct across day sections that club the
                              // same campaign+type.
                              const clubId = `${group.label}-${index}:${entry.club.key}`;
                              return (
                                <li key={`club-${clubId}`} className="w-full">
                                  <ClubbedActivityCard
                                    club={entry.club}
                                    expanded={expanded.has(clubId)}
                                    onToggleExpand={() => toggleExpand(clubId)}
                                    onItemClick={(n) => goTo(n)}
                                    onItemDelete={(n) => deleteNotif(n.id)}
                                  />
                                </li>
                              );
                            }
                            const n = entry.item;
                            return n.count > 1 ? (
                              <li key={n.id} className="w-full">
                                <BatchedActivityCard
                                  notification={n}
                                  expanded={expanded.has(n.id)}
                                  onToggleExpand={() => toggleExpand(n.id)}
                                  onClick={() => goTo(n)}
                                  onDelete={() => deleteNotif(n.id)}
                                  onActorClick={(username) => goToActor(n, username)}
                                />
                              </li>
                            ) : (
                              <li key={n.id} className="w-full">
                                <ActivityRow notification={n} onClick={() => goTo(n)} onDelete={() => deleteNotif(n.id)} />
                              </li>
                            );
                          })}
                    </ul>
                  </React.Fragment>
                ))}
                {hasMore && (
                  <button
                    type="button"
                    onClick={() => void loadMore()}
                    disabled={loading}
                    className="w-full py-3 text-center text-body-sm-semibold text-(--brand-500) hover:text-(--brand-600) disabled:opacity-50"
                  >
                    {loading ? 'Loading…' : 'Load more'}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function EmptyState({ tab }: { tab: NotificationTab }) {
  const copy =
    tab === 'messages'
      ? { title: 'No messages yet', body: 'Replies and comments from your team will show up here.' }
      : { title: 'No recent activity', body: 'Approvals, updates, and status changes will appear here as they happen.' };
  const Icon = tab === 'messages' ? ChatCircle : ClipboardText;
  return (
    <div className="flex w-full flex-col items-center px-4 pb-16 pt-14 text-center">
      <span className="mb-3 flex size-16 items-center justify-center rounded-[16px] bg-[#f4f4f2]">
        <Icon className="size-7 text-[#0e0c0a]" />
      </span>
      <p className="text-[14px] font-medium leading-5 text-[#0e0c0a]">{copy.title}</p>
      <p className="mt-1 max-w-[254px] text-[12px] leading-4 text-[#79736d]">{copy.body}</p>
    </div>
  );
}

// ClubbedActivityCard renders a display-time club (see notificationClubs.ts):
// N same campaign+type per-creator docs from one day section. Unlike
// BatchedActivityCard (one legacy write-time-coalesced doc), every expanded
// row here is a real notification — individually navigable, mark-readable
// (row click) and deletable, so the club shrinks as members are deleted and
// un-bolds per member as they are read.
function ClubbedActivityCard({
  club,
  expanded,
  onToggleExpand,
  onItemClick,
  onItemDelete,
}: {
  club: NotificationClub;
  expanded: boolean;
  onToggleExpand: () => void;
  onItemClick: (n: AppNotification) => void;
  onItemDelete: (n: AppNotification) => void;
}) {
  const first = club.items[0];
  const unreadCount = club.items.filter((n) => !n.read).length;
  const headline = clubHeadline(club);
  if (!first) return null;

  return (
    <div className="w-full overflow-hidden rounded-[12px] border border-[#e5e5e5] bg-white p-0">
      <div
        className="group flex w-full cursor-pointer items-center justify-between gap-2 p-3 transition-colors hover:bg-accent"
        onClick={onToggleExpand}
      >
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="relative flex size-8 shrink-0 items-center justify-center rounded-full bg-[#ecebe9]">
            <ActivityIcon type={first.type} className="size-4.5 text-[#0e0c0a]" />
            {unreadCount > 0 && <span className="absolute -right-0.5 -top-0.5 size-2 rounded-full bg-(--brand-600)" />}
          </span>
          <div className="min-w-0">
            <p
              className={cn(
                'line-clamp-3 text-[13px] leading-[18px] text-[#0e0c0a]',
                unreadCount > 0 ? 'font-semibold' : 'font-medium',
              )}
            >
              {headline}
            </p>
            {first.title && <p className="line-clamp-2 text-[12px] leading-4 text-[#79736d]">{first.title}</p>}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <DismissButton onDelete={() => club.items.forEach((n) => onItemDelete(n))} />
          <button
            type="button"
            aria-expanded={expanded}
            aria-label="Toggle details"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand();
            }}
            className="shrink-0 rounded-full p-1 text-[#79736d] hover:bg-accent"
          >
            <CaretDown size={14} className={cn('transition-transform', expanded && 'rotate-180')} />
          </button>
        </div>
      </div>

      {expanded && (
        <>
          <div className="h-px w-full bg-[#ecebe9]" />
          <ul
            className={cn(
              'flex w-full flex-col',
              club.items.length > MAX_VISIBLE_CLUB_ROWS && 'max-h-[264px] overflow-y-auto',
            )}
          >
            {club.items.map((n) => (
              <li key={n.id} className="w-full">
                <div
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onItemClick(n);
                    }
                  }}
                  className="group/row flex w-full min-h-[42px] cursor-pointer items-center justify-between gap-2 py-2 pl-3 pr-2.5 transition-colors hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
                  onClick={() => onItemClick(n)}
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <InitialsAvatar name={memberName(n)} className="size-6 text-[10px]" />
                    <div className="min-w-0">
                      <p
                        className={cn(
                          'line-clamp-2 text-[12px] leading-4 text-[#0e0c0a]',
                          n.read ? 'font-medium' : 'font-semibold',
                        )}
                      >
                        {memberName(n) ?? 'A creator'}
                      </p>
                      {n.actor_action_line && (
                        <p className="line-clamp-2 text-[12px] leading-4 text-[#79736d]">{n.actor_action_line}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    {!n.read && <span className="size-1.5 rounded-full bg-(--brand-600)" />}
                    <span className="text-[11px] leading-4 text-[#79736d]">
                      {relativeTime(n.last_event_at || n.created_at)}
                    </span>
                    <DismissButton
                      onDelete={() => onItemDelete(n)}
                      className="opacity-0 group-hover/row:opacity-100"
                    />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function ActivityRow({
  notification: n,
  onClick,
  onDelete,
}: {
  notification: AppNotification;
  onClick: () => void;
  onDelete: () => void;
}) {
  const name = memberName(n);
  return (
    <div
      className="group flex w-full cursor-pointer items-center justify-between rounded-[12px] border border-[#ecebe9] bg-white p-3 transition-colors hover:bg-accent"
      onClick={onClick}
    >
      <div className="flex min-w-0 items-center gap-2.5">
        {name ? (
          // Industry anatomy (FB/LinkedIn): lead with the person, overlay the
          // activity type as a small badge.
          <span className="relative shrink-0">
            <InitialsAvatar name={name} className="size-8 text-[13px]" />
            <span className="absolute -bottom-0.5 -right-0.5 flex size-4 items-center justify-center rounded-full bg-[#ecebe9] ring-2 ring-white">
              <ActivityIcon type={n.type} className="size-2.5 text-[#0e0c0a]" />
            </span>
          </span>
        ) : (
          <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[#ecebe9]">
            <ActivityIcon type={n.type} className="size-4.5 text-[#0e0c0a]" />
          </span>
        )}
        <div className="min-w-0">
          <p
            className={cn(
              'line-clamp-3 text-[13px] leading-[18px] text-[#0e0c0a]',
              n.read ? 'font-medium' : 'font-semibold',
            )}
          >
            {n.message || n.title}
          </p>
          {n.title && <p className="line-clamp-2 text-[12px] leading-4 text-[#79736d]">{n.title}</p>}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        {!n.read && <span className="size-1.5 rounded-full bg-(--brand-600)" />}
        <span className="text-[12px] leading-4 text-[#79736d]">{relativeTime(n.last_event_at || n.created_at)}</span>
        <DismissButton onDelete={onDelete} />
      </div>
    </div>
  );
}

function BatchedActivityCard({
  notification: n,
  expanded,
  onToggleExpand,
  onClick,
  onDelete,
  onActorClick,
}: {
  notification: AppNotification;
  expanded: boolean;
  onToggleExpand: () => void;
  onClick: () => void;
  onDelete: () => void;
  onActorClick: (username: string) => void;
}) {
  const actors = normalizeSampleActors(n.sample_actors);
  // Actor count drives the plural headline: the deduped list length when
  // present, falling back to the raw event counter (`count`) for a
  // pre-backfill notification with no sample_actors at all. `count` can
  // still exceed `actors.length` post-backfill too — the list is capped at
  // 25 — hence the separate "and N more" line below.
  const k = actors.length || n.count;
  const headline = (k === 1 ? null : n.batch_label ? `${k} creators ${n.batch_label}` : null) ?? (n.message || n.title);
  const actionLine = n.actor_action_line;
  const moreCount = n.count - actors.length;

  return (
    <div className="group w-full overflow-hidden rounded-[12px] border border-[#e5e5e5] bg-white p-0">
      <div className="flex w-full items-center justify-between p-3">
        <div className="flex min-w-0 cursor-pointer items-center gap-2.5" onClick={onClick}>
          <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[#ecebe9]">
            <ActivityIcon type={n.type} className="size-4.5 text-[#0e0c0a]" />
          </span>
          <div className="min-w-0">
            <p className="line-clamp-3 text-[13px] font-medium leading-[18px] text-[#0e0c0a]">{headline}</p>
            {n.title && <p className="line-clamp-2 text-[12px] leading-4 text-[#79736d]">{n.title}</p>}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <DismissButton onDelete={onDelete} />
          <button
            type="button"
            aria-expanded={expanded}
            aria-label="Toggle details"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand();
            }}
            className="flex shrink-0 items-center justify-center rounded-full p-1 text-[#79736d] transition-colors hover:bg-accent"
          >
            <CaretDown size={14} className={cn('size-3.5 transition-transform', expanded && 'rotate-180')} />
          </button>
        </div>
      </div>
      {expanded && (
        <>
          <div className="h-px w-full bg-[#ecebe9]" />
          <div
            className={cn('flex w-full flex-col', actors.length > MAX_VISIBLE_CLUB_ROWS && 'max-h-[264px] overflow-y-auto')}
          >
            {actors.map((actor, idx) => {
              const rowContent = (
                <div className="flex min-w-0 items-center gap-2.5">
                  <InitialsAvatar name={actor.name} className="size-6 text-[10px]" />
                  <div className="min-w-0">
                    <p className="line-clamp-2 text-[12px] font-medium leading-4 text-[#0e0c0a]">{actor.name}</p>
                    {actionLine && <p className="line-clamp-2 text-[12px] leading-4 text-[#79736d]">{actionLine}</p>}
                  </div>
                </div>
              );
              // Only actors with a resolvable username can be navigated to —
              // rows without one (e.g. legacy plain-string actors pre-backfill)
              // stay static.
              if (actor.username) {
                const username = actor.username;
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => onActorClick(username)}
                    className="flex min-h-[42px] w-full items-center justify-between py-2 pl-3 pr-3 text-left hover:bg-accent"
                  >
                    {rowContent}
                  </button>
                );
              }
              return (
                <div key={idx} className="flex min-h-[42px] w-full items-center justify-between py-2 pl-3 pr-3">
                  {rowContent}
                </div>
              );
            })}
          </div>
          {/* Only meaningful for genuinely multi-actor batches: with per-
              subject group keys a count>1 doc can be one creator repeating
              (script v2), where "and 1 more" would misread as another
              creator. */}
          {actors.length > 1 && n.count > actors.length && (
            <div className="flex h-[46px] w-full items-center pl-4 text-[12px] text-[#79736d]">and {moreCount} more</div>
          )}
        </>
      )}
    </div>
  );
}

function MessageRow({
  notification: n,
  onRowClick,
  onFocusMarkRead,
  onDelete,
}: {
  notification: AppNotification;
  onRowClick: () => void;
  onFocusMarkRead: () => void;
  onDelete: () => void;
}) {
  const [reply, setReply] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [locallyLocked, setLocallyLocked] = React.useState(false);
  const canReply = !!n.entry_id && !n.thread_locked && !locallyLocked;

  const send = async () => {
    if (!canReply || !reply.trim() || sending) return;
    setSending(true);
    try {
      await conversations.sendMessage(n.entry_id as string, reply.trim(), n.thread_type ?? 'selection');
      setReply('');
      toast.success('Reply sent');
    } catch (err) {
      if (isThreadLockedError(err)) {
        setLocallyLocked(true);
        toast.error('This conversation is locked');
      } else {
        toast.error(err instanceof Error ? err.message : 'Failed to send reply');
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="group flex w-full cursor-pointer flex-col gap-2 rounded-[12px] border border-[#e5e5e5] bg-white p-3 transition-colors hover:bg-accent"
      onClick={onRowClick}
    >
      <div className="flex w-full items-start justify-between">
        <div className="flex min-w-0 items-center gap-2.5">
          <InitialsAvatar name={n.creator_name ?? normalizeSampleActors(n.sample_actors)[0]?.name} className="size-8 text-[13px]" />
          <div className="min-w-0">
            <p
              className={cn(
                'line-clamp-2 text-[13px] leading-[18px] text-[#0e0c0a]',
                n.read ? 'font-medium' : 'font-semibold',
              )}
            >
              {n.creator_name ?? n.title}
            </p>
            {n.creator_name && <p className="line-clamp-2 text-[12px] leading-4 text-[#79736d]">{n.title}</p>}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {!n.read && <span className="size-1.5 rounded-full bg-(--brand-600)" />}
          <span className="text-[12px] leading-4 text-[#79736d]">{relativeTime(n.last_event_at || n.created_at)}</span>
          <DismissButton onDelete={onDelete} />
        </div>
      </div>
      <div className="w-full rounded-[8px] bg-[#f4f4f5] px-3 py-2 text-[12px] leading-4 text-[#737373] line-clamp-3">
        {n.teaser ?? n.message}
      </div>
      {canReply && (
        <div
          className="flex w-full items-center justify-between gap-2 rounded-full border border-[#e0dedb] bg-white py-2 pl-4 pr-2"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="text"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onFocus={onFocusMarkRead}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void send();
            }}
            placeholder={n.last_sender_name ? `Reply to ${n.last_sender_name}…` : 'Reply…'}
            className="min-w-0 flex-1 bg-transparent text-[12px] leading-4 outline-none placeholder:text-[#79736d]"
          />
          <button
            type="button"
            aria-label="Send reply"
            onClick={(e) => {
              e.stopPropagation();
              void send();
            }}
            disabled={!reply.trim() || sending}
            className="shrink-0 rounded-full bg-[#18181b] p-1.5 text-white disabled:opacity-50"
          >
            <ArrowUpRight size={14} className="size-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
